import {
  ServWithMods,
  ServiceWithPricing,
  EquipmentQuantity,
  QuoteFullyPopulated,
  Inspector,
  RequiredInfoValue,
} from '@/util/types/serverTypes';
import { AddressForScheduler } from '@/util/types/globalTypes';
import {
  Bundle,
  DiscountCode,
} from '@/util/types/serverTypeCollection/discounts';
import { calculateServicePrices } from './calculateServicePrices';

/**
 * Temporary function that mirrors the exact logic from NewSchedulingForm useEffect (lines 481-743)
 * Used for creating baseline snapshot tests before refactoring
 * 
 * @param params - All inputs needed for the calculation
 * @returns Calculated services, equipment, and whether equipment changed
 */
export function calculatePriceWithEquipment(params: {
  selectedServiceIds: string[];
  effectiveServices: ServWithMods[];
  address: AddressForScheduler | null;
  expandedAreaPrice: number;
  requestedInspector: boolean;
  requiredInfoValues: RequiredInfoValue[];
  equipmentQuantities: EquipmentQuantity[];
  scheduledInspector: Inspector | null;
  bundle: Bundle | null;
  discountCode: DiscountCode | null;
  appliedDiscountServiceIds: string[];
  previousServices?: ServiceWithPricing[];
  quote?: QuoteFullyPopulated;
}): {
  services: ServiceWithPricing[];
  equipment: EquipmentQuantity[];
  hasEquipmentChanges: boolean;
} {
  const {
    selectedServiceIds,
    effectiveServices,
    address,
    expandedAreaPrice,
    requestedInspector,
    requiredInfoValues,
    equipmentQuantities,
    scheduledInspector,
    bundle,
    discountCode,
    appliedDiscountServiceIds,
    previousServices,
    quote,
  } = params;

  // Build selected services with isPrimary flag (lines 490-503)
  const selectedServicesFinal = selectedServiceIds
    .map((x) => effectiveServices.find((y) => y._id === x))
    .map((service) => {
      if (service) {
        return {
          ...service,
          isPrimary: selectedServiceIds[0] === service._id,
        };
      }
      return service;
    })
    .filter((x) => x !== undefined) as (ServWithMods & {
    isPrimary: boolean;
  })[];

  // Early return if no address (lines 506, 728-730)
  if (!address) {
    return {
      services: selectedServicesFinal,
      equipment: equipmentQuantities,
      hasEquipmentChanges: false,
    };
  }

  // FIRST PASS: Calculate with current equipment (lines 508-529)
  const firstPassServices = calculateServicePrices(
    selectedServicesFinal,
    address,
    expandedAreaPrice,
    requestedInspector,
    requiredInfoValues,
    equipmentQuantities,
    selectedServiceIds,
    [...(scheduledInspector?._id ? [scheduledInspector._id] : [])],
    {
      bundle,
      discountCode: discountCode
        ? {
            ...(discountCode as any),
            _serviceIds: appliedDiscountServiceIds || [],
          }
        : undefined,
    }
  );

  // Extract equipment adjustments (lines 532-538)
  const allEquipmentAdjustments: EquipmentQuantity[] = firstPassServices
    .map((service) =>
      service.modifierCalc && 'equipmentAdjustments' in service.modifierCalc
        ? service.modifierCalc.equipmentAdjustments
        : []
    )
    .flat() as EquipmentQuantity[];

  // Calculate updated equipment quantities (lines 540-605)
  let updatedEquipmentQuantities = equipmentQuantities;
  let needsSecondPass = false;
  let hasEquipmentChanges = false;

  if (allEquipmentAdjustments.length > 0) {
    const quantityMap = new Map<string, EquipmentQuantity>();

    // Start with existing equipment quantities
    equipmentQuantities.forEach((eq) => {
      quantityMap.set(eq._equipmentCategoryId, { ...eq });
    });

    // Apply adjustments respecting manualChange flag
    allEquipmentAdjustments.forEach((adjustment: EquipmentQuantity) => {
      const existing = quantityMap.get(adjustment._equipmentCategoryId);

      if (existing) {
        if (!existing.manualChange) {
          const newValue = Math.max(0, adjustment.value);
          if (existing.value !== newValue) {
            existing.value = newValue;
            needsSecondPass = true;
          }
        }
      } else {
        if (adjustment.value > 0) {
          quantityMap.set(adjustment._equipmentCategoryId, {
            ...adjustment,
            manualChange: false,
          });
          needsSecondPass = true;
        }
      }
    });

    // Get updated equipment quantities
    updatedEquipmentQuantities = Array.from(quantityMap.values()).filter(
      (eq) => eq.value > 0 || eq.manualChange
    );

    // Check for meaningful changes
    hasEquipmentChanges =
      updatedEquipmentQuantities.length !== equipmentQuantities.length ||
      updatedEquipmentQuantities.some((newEq) => {
        const existing = equipmentQuantities.find(
          (eq) => eq._equipmentCategoryId === newEq._equipmentCategoryId
        );
        return (
          !existing ||
          existing.value !== newEq.value ||
          existing.manualChange !== newEq.manualChange
        );
      });

    needsSecondPass = hasEquipmentChanges;
  }

  // SECOND PASS: Recalculate if needed (lines 608-631)
  const calculatedServices = needsSecondPass
    ? calculateServicePrices(
        selectedServicesFinal,
        address,
        expandedAreaPrice,
        requestedInspector,
        requiredInfoValues,
        updatedEquipmentQuantities,
        selectedServiceIds,
        [...(scheduledInspector?._id ? [scheduledInspector._id] : [])],
        {
          bundle,
          discountCode: discountCode
            ? {
                ...(discountCode as any),
                _serviceIds: appliedDiscountServiceIds || [],
              }
            : undefined,
        }
      )
    : firstPassServices;

  // Apply price overrides (lines 650-687)
  let servicesWithOverrides: ServiceWithPricing[];
  if (!previousServices || previousServices.length === 0) {
    // First calculation - initialize from quote if present
    if (!quote) {
      servicesWithOverrides = calculatedServices;
    } else {
      servicesWithOverrides = calculatedServices.map((newService) => {
        const quoteService = quote.charges.find(
          (p) => p._serviceId?._id === newService._id
        );
        if (quoteService && quoteService.amount !== undefined) {
          return {
            ...newService,
            priceOverride:
              quoteService.amount !== newService.totalPrice
                ? quoteService.amount
                : undefined,
            totalPrice: quoteService.amount,
            chargeId: quoteService._id,
          };
        }
        return newService;
      });
    }
  } else {
    // Preserve overrides from previous calculation
    servicesWithOverrides = calculatedServices.map((newService) => {
      const prevService = previousServices.find((p) => p._id === newService._id);
      if (prevService && prevService.priceOverride !== undefined) {
        return {
          ...newService,
          priceOverride: prevService.priceOverride,
          totalPrice: prevService.priceOverride,
          chargeId: prevService.chargeId,
        };
      }
      return { ...newService, chargeId: prevService?.chargeId };
    });
  }

  // Annotate bundle / discount metadata (lines 689-727)
  const bundleServiceIds = new Set<string>(
    bundle
      ? [
          ...(bundle.items?.map((i) => i._serviceId) || []),
          ...(bundle._primaryServiceId || []),
        ]
      : []
  );
  const triggeringPrimaryId = bundle
    ? selectedServiceIds.find((id) =>
        (bundle._primaryServiceId || []).includes(id)
      )
    : undefined;

  const finalServices = servicesWithOverrides.map((svc) => {
    const participatesInBundle =
      !!bundle &&
      (bundleServiceIds.has(svc._id) || svc._id === triggeringPrimaryId);
    const hasDiscountCode = !!discountCode;
    return {
      ...svc,
      ...(participatesInBundle
        ? {
            appliedBundleId: bundle!._id,
            bundleName: bundle!.name,
          }
        : {}),
      ...(hasDiscountCode
        ? {
            appliedDiscountCodeId: discountCode!._id,
            discountCodeName: discountCode!.code,
          }
        : {}),
    };
  });

  return {
    services: finalServices,
    equipment: updatedEquipmentQuantities,
    hasEquipmentChanges,
  };
}

